import {
  BellOutlined,
  CheckOutlined,
  ExportOutlined,
  MutedOutlined,
  SoundOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGuardedNavigate } from "../../../../shared/leaveGuard/LeaveGuardProvider";
import { ReactComponent as NotificationsIcon } from "../../../assets/svgIcon/topBar/notifications.svg";
import {
  getNotificationRoute,
  isNotificationSoundMuted,
  setNotificationSoundMuted,
} from "../../../../notification/helpers";
import { timeStampInCurrentTZ } from "../../../../../helper/utils";
import { useNotification } from "../../../../../hooks/useNotification";
import {
  DEFAULT_PAGE_SIZE,
  notificationApiSlice,
  useLazyGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  usePatchNotificationToIsreadMutation,
} from "../../../../../store/apiSlice/notificationApiSlice";
import { setSoundMuted } from "../../../../../store/slice/miscSlice";
import { PopupModal } from "../PopupModal/PopupModal";
import {
  StyledEmptyState,
  StyledErrorState,
  StyledLoader,
  StyledNotificationBell,
  StyledNotificationPanel,
  StyledNotificationRow,
  StyledScrollHint,
  StyledSpinner,
} from "./NotificationBell.styles";

/**
 * Format unread count for the toolbar badge.
 *
 * @param {number} count
 * @return {string}
 */
function formatBadgeCount(count) {
  if (count > 99) {
    return "99+";
  }
  return String(count);
}

/**
 * Skeleton rows shown only on the first inbox load.
 *
 * @return {JSX.Element}
 */
const NotificationLoader = () => (
  <StyledLoader aria-busy="true" aria-label="Loading notifications">
    {[0, 1, 2].map(index => (
      <div className="row" key={`skeleton-${index}`}>
        <div className="line short" />
        <div className="line long" />
        <div className="line medium" />
      </div>
    ))}
  </StyledLoader>
);

const NotificationRow = ({ item, onAfterNavigate }) => {
  const navigate = useGuardedNavigate();
  const notify = useNotification();
  const { id, is_read, notification } = item || {};
  const { content, date_of_creation, relative_url, ntf_type } = notification || {};
  const [patchNotificationToIsread] = usePatchNotificationToIsreadMutation();

  const markAsRead = async () => {
    if (is_read) {
      return;
    }
    try {
      await patchNotificationToIsread({ id, is_read: true }).unwrap();
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Failed to mark notification as read.",
      });
    }
  };

  const route = getNotificationRoute(relative_url);

  const openLinkedPage = event => {
    event.stopPropagation();
    if (!route) {
      return;
    }
    onAfterNavigate?.();
    navigate(route);
  };

  return (
    <StyledNotificationRow $clickable={!is_read} $unread={!is_read}>
      <span aria-hidden="true" className="unreadDot" />
      <div
        className="body"
        onClick={markAsRead}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            markAsRead();
          }
        }}
        role={!is_read ? "button" : undefined}
        tabIndex={!is_read ? 0 : undefined}
      >
        {ntf_type?.source || ntf_type?.name ? (
          <div className="meta">
            {[ntf_type?.source, ntf_type?.name].filter(Boolean).join(" · ")}
          </div>
        ) : null}
        <div className="content">{content}</div>
        <div className="time">
          {timeStampInCurrentTZ(date_of_creation).format("MMM DD, YYYY. hh:mm a")}
        </div>
      </div>
      {route ? (
        <button
          aria-label="Open related page"
          className="openLink"
          onClick={openLinkedPage}
          type="button"
        >
          <ExportOutlined />
        </button>
      ) : null}
    </StyledNotificationRow>
  );
};

const NotificationPanel = ({
  notifications,
  unreadCount,
  soundMuted,
  onToggleSoundMute,
  onClose,
  showFirstLoadLoader,
  isError,
  onRetry,
}) => {
  const notify = useNotification();
  const hasMore = useSelector(state => state.misc.notificationsHasMore);
  const [loadMore, { isFetching }] = useLazyGetNotificationsQuery();
  const [markAllNotificationsAsRead, { isLoading: isMarkingAll }] =
    useMarkAllNotificationsAsReadMutation();
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadGuardRef = useRef({ inFlight: false, lastOffset: -1 });
  const hasItems = Array.isArray(notifications) && notifications.length > 0;

  // After a page-0 resync the list can shrink; allow that offset to load again.
  useEffect(() => {
    if (notifications.length < loadGuardRef.current.lastOffset) {
      loadGuardRef.current.lastOffset = -1;
    }
  }, [notifications.length]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = listRef.current;
    if (!node || !root || showFirstLoadLoader || !hasMore) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (
          !entries[0]?.isIntersecting ||
          !hasMore ||
          isFetching ||
          loadGuardRef.current.inFlight
        ) {
          return;
        }
        const offset = notifications.length;
        if (offset === loadGuardRef.current.lastOffset) {
          return;
        }
        loadGuardRef.current.inFlight = true;
        loadGuardRef.current.lastOffset = offset;
        loadMore({ limit: DEFAULT_PAGE_SIZE, offset })
          .unwrap()
          .catch(() => {
            // Allow retry of this offset after a failed request.
            loadGuardRef.current.lastOffset = -1;
          })
          .finally(() => {
            loadGuardRef.current.inFlight = false;
          });
      },
      { root, threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetching, loadMore, notifications.length, showFirstLoadLoader]);

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead().unwrap();
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Failed to mark all notifications as read.",
      });
    }
  };

  const subtitle = showFirstLoadLoader
    ? "Fetching your latest updates…"
    : unreadCount > 0
      ? `${unreadCount} unread`
      : "You're all caught up";

  return (
    <StyledNotificationPanel onClick={event => event.stopPropagation()}>
      <div className="ntfPanelHeader">
        <div className="titles">
          <h3>Notifications</h3>
          <p className="subtitle">{subtitle}</p>
        </div>
        <div className="headerActions">
          {hasItems && unreadCount > 0 ? (
            <button
              aria-label="Mark all as read"
              className="headerIconBtn"
              disabled={isMarkingAll}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                markAllAsRead();
              }}
              title="Mark all as read"
              type="button"
            >
              <CheckOutlined />
            </button>
          ) : null}
          <button
            aria-label={soundMuted ? "Unmute notification sound" : "Mute notification sound"}
            className="headerIconBtn"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onToggleSoundMute();
            }}
            title={soundMuted ? "Unmute sound" : "Mute sound"}
            type="button"
          >
            {soundMuted ? <MutedOutlined /> : <SoundOutlined />}
          </button>
        </div>
      </div>

      <div className="ntfPanelList" ref={listRef}>
        {showFirstLoadLoader ? (
          <NotificationLoader />
        ) : isError && !hasItems ? (
          <StyledErrorState>
            <div className="iconWrap">
              <WarningOutlined />
            </div>
            <h4>Couldn’t load notifications</h4>
            <p>Check your connection and try again.</p>
            <button className="retryBtn" onClick={onRetry} type="button">
              Retry
            </button>
          </StyledErrorState>
        ) : hasItems ? (
          <>
            {notifications.map((item, index) => (
              <NotificationRow
                key={item?.id ?? `ntf-${index}`}
                item={item}
                onAfterNavigate={onClose}
              />
            ))}
            {hasMore ? (
              <StyledScrollHint ref={sentinelRef}>
                {isFetching || loadGuardRef.current.inFlight ? (
                  <>
                    <StyledSpinner aria-hidden="true" />
                    Loading more
                  </>
                ) : (
                  "Scroll for more"
                )}
              </StyledScrollHint>
            ) : null}
          </>
        ) : (
          <StyledEmptyState>
            <div className="iconWrap">
              <BellOutlined />
            </div>
            <h4>No notifications yet</h4>
            <p>
              When something needs your attention, it will show up here so you can act on it
              quickly.
            </p>
          </StyledEmptyState>
        )}
      </div>
    </StyledNotificationPanel>
  );
};

/**
 * TopBar notification control. Panel is absolute under the icon, same as Create (+).
 *
 * @return {JSX.Element}
 */
export const NotificationBell = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const notifications = useSelector(state => state.misc.notifications);
  const unreadCount = useSelector(state => state.misc.unreadCount);
  const soundMuted = useSelector(state => state.misc.soundMuted);

  // Read status only — Bootstrap owns the single page-0 subscription/fetch.
  const { isLoading, isFetching, isError, isSuccess, isUninitialized } =
    notificationApiSlice.endpoints.getNotifications.useQueryState({
      limit: DEFAULT_PAGE_SIZE,
      offset: 0,
    });

  const [refreshNotifications] = useLazyGetNotificationsQuery();

  // PopupModal closes on document mousedown before the icon's click fires, so a
  // plain toggle would reopen the panel. Remember the state at press time.
  const wasOpenOnPressRef = useRef(false);

  const closePopover = () => setIsOpen(false);
  const togglePopover = () => setIsOpen(prev => !prev);

  const handleIconMouseDown = () => {
    wasOpenOnPressRef.current = isOpen;
  };

  const handleIconClick = () => {
    const wasOpen = wasOpenOnPressRef.current;
    wasOpenOnPressRef.current = false;
    setIsOpen(!wasOpen);
  };

  const handleIconKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      togglePopover();
    } else if (event.key === "Escape") {
      closePopover();
    }
  };

  const toggleSoundMute = () => {
    const next = !isNotificationSoundMuted();
    setNotificationSoundMuted(next);
    dispatch(setSoundMuted(next));
  };

  const handleRetry = () => {
    // List payload includes unread_count — no separate unread API.
    refreshNotifications({ limit: DEFAULT_PAGE_SIZE, offset: 0 });
  };

  // Mute is sound-only — unread badge stays visible.
  const showBadge = unreadCount > 0;
  // Skeleton only until the first app-level inbox response settles.
  const showFirstLoadLoader =
    (isUninitialized || isLoading || (isFetching && !isSuccess && !isError)) &&
    !(Array.isArray(notifications) && notifications.length > 0);

  return (
    <StyledNotificationBell className="item">
      <NotificationsIcon
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={
          soundMuted
            ? "Notifications, sound muted"
            : unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
        }
        onClick={handleIconClick}
        onKeyDown={handleIconKeyDown}
        onMouseDown={handleIconMouseDown}
        role="button"
        tabIndex={0}
      />
      {soundMuted ? <span aria-hidden="true" className="muteSlash" /> : null}
      {showBadge ? <span className="badge">{formatBadgeCount(unreadCount)}</span> : null}

      {isOpen ? (
        <PopupModal closeModal={closePopover}>
          <NotificationPanel
            isError={isError}
            notifications={notifications}
            onClose={closePopover}
            onRetry={handleRetry}
            onToggleSoundMute={toggleSoundMute}
            showFirstLoadLoader={showFirstLoadLoader}
            soundMuted={soundMuted}
            unreadCount={unreadCount}
          />
        </PopupModal>
      ) : null}
    </StyledNotificationBell>
  );
};
